import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

export interface PdfExportOptions {
  filename?: string;
  quality?: number;
  scale?: number;
}

/**
 * Generate a PDF from a React component element
 */
export async function generatePdfFromElement(
  element: HTMLElement,
  options: PdfExportOptions = {}
): Promise<void> {
  const {
    filename = `ai-sizing-report-${new Date().toISOString().split('T')[0]}.pdf`,
    quality = 0.95,
    scale = 2,
  } = options;

  // Wait for fonts and images to load
  await document.fonts.ready;
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Generate high-quality PNG from the element
  const dataUrl = await toPng(element, {
    quality,
    pixelRatio: scale,
    backgroundColor: '#ffffff',
    style: {
      transform: 'scale(1)',
      transformOrigin: 'top left',
    },
  });

  // Create PDF (A4 dimensions in mm: 210 x 297)
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 0;

  // Load the image to get dimensions
  const img = new Image();
  img.src = dataUrl;
  await new Promise((resolve) => {
    img.onload = resolve;
  });

  // Calculate scaling to fit page width
  const imgWidth = pageWidth - margin * 2;
  const imgHeight = (img.height * imgWidth) / img.width;

  // Handle multi-page if content is taller than one page
  const totalPages = Math.ceil(imgHeight / (pageHeight - margin * 2));

  if (totalPages === 1) {
    pdf.addImage(dataUrl, 'PNG', margin, margin, imgWidth, imgHeight);
  } else {
    // For multi-page, we need to slice the image
    let remainingHeight = imgHeight;
    let sourceY = 0;
    const pageContentHeight = pageHeight - margin * 2;

    for (let page = 0; page < totalPages; page++) {
      if (page > 0) {
        pdf.addPage();
      }

      const sliceHeight = Math.min(pageContentHeight, remainingHeight);

      // Add the full image but position it to show only the current slice
      pdf.addImage(
        dataUrl,
        'PNG',
        margin,
        margin - sourceY * (imgWidth / img.width),
        imgWidth,
        imgHeight
      );

      sourceY += sliceHeight / (imgWidth / img.width);
      remainingHeight -= pageContentHeight;
    }
  }

  // Download the PDF
  pdf.save(filename);
}

/**
 * Generate filename with timestamp
 */
export function generatePdfFilename(prefix = 'ai-sizing-report'): string {
  const date = new Date();
  const timestamp = date.toISOString().split('T')[0];
  return `${prefix}-${timestamp}.pdf`;
}
